import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { RestrictedGateway } from 'src/features/membershipType/types';
import RestrictedGatewayForm from 'src/features/membership/components/forms/restrictedGateway/RestrictedGatewayForm';
import {
    restrictedGatewayFormSchema,
    RestrictedGatewayFormSchemaValues,
} from 'src/features/membershipType/formSchema';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { useParking } from 'src/providers/ParkingProvider';

interface RestrictedGatewayFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedRestrictedGateway?: RestrictedGateway;
    membershipTypeRestrictedGateways?: RestrictedGateway[];
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: { restricted_gateways: string[] },
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
    membershipTypeId: string;
}

function RestrictedGatewayFormModal<TData>({
    selectedRestrictedGateway,
    membershipTypeRestrictedGateways,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
    membershipTypeId,
}: RestrictedGatewayFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();
    const onError = useOnError();

    const { data: gatewayListData } = useListGateways({
        parkingId,
        restricted_membership_type: membershipTypeId,
    });

    const defaultValues = React.useMemo(() => {
        return {
            gatewayId: selectedRestrictedGateway?.id ?? undefined,
        };
    }, [selectedRestrictedGateway]);

    const form = useForm<RestrictedGatewayFormSchemaValues>({
        resolver: zodResolver(restrictedGatewayFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [selectedRestrictedGateway, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const restrictedGatewayIds = React.useMemo(() => {
        return membershipTypeRestrictedGateways?.map((gateway) => gateway.id) ?? [];
    }, [membershipTypeRestrictedGateways]);

    const onSubmit = React.useCallback(
        (values: RestrictedGatewayFormSchemaValues) => {
            mutate(
                { restricted_gateways: [...restrictedGatewayIds, values.gatewayId] },
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [mutate, form, onError, setIsModalOpen, restrictedGatewayIds],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild disabled={gatewayListData?.count === 0}>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <RestrictedGatewayForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedRestrictedGateway && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default RestrictedGatewayFormModal;
